import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Phone, Envelope, Clock, InstagramLogo, FacebookLogo, PaperPlaneRight } from '@phosphor-icons/react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs obligatoires' : 'Please fill all required fields');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(language === 'fr' ? 'Message envoyé avec succès !' : language === 'it' ? 'Messaggio inviato con successo!' : 'Message sent successfully!');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi. Veuillez réessayer.' : 'Error sending message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen pb-20 bg-[#000000]" data-testid="contact-page">
      {/* Header */}
      <div className="bg-[#000000] pt-28 sm:pt-36 pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-white font-['Playfair_Display'] mb-4">
            {t('contact.title')}
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl">
            {language === 'fr' ? "N'hésitez pas à nous contacter pour toute question ou réservation" : language === 'it' ? "Non esitate a contattarci per qualsiasi domanda" : "Don't hesitate to contact us for any question or booking"}
          </p>
        </div>
      </div>

      <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Left: Contact Info Cards */}
          <div className="lg:col-span-2 space-y-5">
            <Card className="card-shadow hover-lift animate-fade-up stagger-1 border-0">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#b7ede0] flex items-center justify-center flex-shrink-0">
                  <MapPin size={24} weight="fill" className="text-[#000000]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#000000] mb-1 text-base">
                    {language === 'fr' ? 'Adresse' : language === 'it' ? 'Indirizzo' : 'Address'}
                  </h3>
                  <p className="text-[#4B5563] text-sm leading-relaxed">Port de Plaisance<br/>20260 Calvi, Corse</p>
                </div>
              </CardContent>
            </Card>

            <Card className="card-shadow hover-lift animate-fade-up stagger-2 border-0">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#b7ede0] flex items-center justify-center flex-shrink-0">
                  <Phone size={24} weight="fill" className="text-[#000000]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#000000] mb-1 text-base">
                    {language === 'fr' ? 'Téléphone' : language === 'it' ? 'Telefono' : 'Phone'}
                  </h3>
                  <a href="tel:+33644383406" className="text-[#000000] hover:text-[#b7ede0] transition-colors text-lg font-medium">
                    06 44 38 34 06
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="card-shadow hover-lift animate-fade-up stagger-3 border-0">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#b7ede0] flex items-center justify-center flex-shrink-0">
                  <Envelope size={24} weight="fill" className="text-[#000000]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#000000] mb-1 text-base">Email</h3>
                  <a href="mailto:contact@calvi-fishing.com" className="text-[#000000] hover:text-[#b7ede0] transition-colors text-lg font-medium">
                    contact@calvi-fishing.com
                  </a>
                </div>
              </CardContent>
            </Card>

            <Card className="card-shadow hover-lift animate-fade-up stagger-4 border-0">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#b7ede0] flex items-center justify-center flex-shrink-0">
                  <Clock size={24} weight="fill" className="text-[#000000]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#000000] mb-1 text-base">
                    {language === 'fr' ? 'Horaires' : language === 'it' ? 'Orari' : 'Hours'}
                  </h3>
                  <p className="text-[#4B5563] text-sm leading-relaxed">
                    {language === 'fr' ? 'Lundi - Vendredi' : language === 'it' ? 'Lunedì - Venerdì' : 'Monday - Friday'} : <span className="font-medium text-[#000000]">6h00 - 20h00</span><br/>
                    {language === 'fr' ? 'Samedi' : language === 'it' ? 'Sabato' : 'Saturday'} : <span className="font-medium text-[#000000]">6h00 - 22h00</span><br/>
                    {language === 'fr' ? 'Dimanche' : language === 'it' ? 'Domenica' : 'Sunday'} : <span className="font-medium text-[#000000]">6h00 - 22h00</span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Social Links */}
            <div className="pt-4 animate-fade-up stagger-5">
              <h3 className="font-semibold text-[#000000] mb-4">{t('contact.followUs')}</h3>
              <div className="flex gap-4">
                <a
                  href="https://www.instagram.com/calvifishing/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-xl bg-[#000000] flex items-center justify-center text-white hover:bg-[#b7ede0] hover:text-[#000000] transition-all duration-200"
                  data-testid="contact-instagram"
                >
                  <InstagramLogo size={24} />
                </a>
                <a
                  href="https://www.facebook.com/calvifishing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-xl bg-[#000000] flex items-center justify-center text-white hover:bg-[#b7ede0] hover:text-[#000000] transition-all duration-200"
                  data-testid="contact-facebook"
                >
                  <FacebookLogo size={24} />
                </a>
              </div>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="lg:col-span-3 animate-fade-up">
            <Card className="card-shadow border-0">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-[#000000] font-['Playfair_Display'] mb-2">
                  {language === 'fr' ? 'Envoyez-nous un message' : language === 'it' ? 'Inviaci un messaggio' : 'Send us a message'}
                </h2>
                <p className="text-[#4B5563] mb-8 text-sm">
                  {language === 'fr' ? 'Nous vous répondrons dans les plus brefs délais' : language === 'it' ? 'Vi risponderemo il prima possibile' : 'We will get back to you as soon as possible'}
                </p>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-[#000000] font-medium mb-2 block">
                        {language === 'fr' ? 'Nom complet' : language === 'it' ? 'Nome completo' : 'Full name'} *
                      </Label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={language === 'fr' ? 'Jean Dupont' : 'John Doe'}
                        className="h-12 rounded-xl border-[#E2E8F0] focus:border-[#b7ede0] focus:ring-[#b7ede0]"
                        data-testid="contact-name"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-[#000000] font-medium mb-2 block">Email *</Label>
                      <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="email@example.com"
                        className="h-12 rounded-xl border-[#E2E8F0] focus:border-[#b7ede0] focus:ring-[#b7ede0]"
                        data-testid="contact-email"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label className="text-[#000000] font-medium mb-2 block">
                        {language === 'fr' ? 'Téléphone' : language === 'it' ? 'Telefono' : 'Phone'}
                      </Label>
                      <Input
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+33 6 44 38 34 06"
                        className="h-12 rounded-xl border-[#E2E8F0] focus:border-[#b7ede0] focus:ring-[#b7ede0]"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <Label className="text-[#000000] font-medium mb-2 block">
                        {language === 'fr' ? 'Objet' : language === 'it' ? 'Oggetto' : 'Subject'}
                      </Label>
                      <Input
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder={language === 'fr' ? 'Renseignement sortie pêche' : 'Fishing trip inquiry'}
                        className="h-12 rounded-xl border-[#E2E8F0] focus:border-[#b7ede0] focus:ring-[#b7ede0]"
                        data-testid="contact-subject"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-[#000000] font-medium mb-2 block">Message *</Label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={language === 'fr' ? 'Votre message...' : language === 'it' ? 'Il tuo messaggio...' : 'Your message...'}
                      rows={6}
                      className="w-full rounded-xl border border-[#E2E8F0] p-4 text-sm resize-none focus:outline-none focus:border-[#b7ede0] focus:ring-1 focus:ring-[#b7ede0] transition-colors"
                      data-testid="contact-message"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-[#000000] hover:bg-[#1a1a1a] text-[#b7ede0] rounded-full py-6 text-base font-semibold gap-2 transition-all duration-200"
                    data-testid="contact-submit-btn"
                  >
                    {sending ? (
                      <>
                        <div className="spinner w-5 h-5 mr-2" />
                        {language === 'fr' ? 'Envoi en cours...' : 'Sending...'}
                      </>
                    ) : (
                      <>
                        <PaperPlaneRight size={20} weight="fill" />
                        {language === 'fr' ? 'Envoyer le message' : language === 'it' ? 'Invia il messaggio' : 'Send message'}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Map below form */}
            <div className="mt-8 rounded-2xl overflow-hidden h-[300px] card-shadow">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2930.5!2d8.755833!3d42.566667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12d0fa6d3e8b9b9b%3A0x8b9b9b9b9b9b9b9b!2sPort%20de%20Calvi!5e0!3m2!1sfr!2sfr!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Calvi Fishing Location"
              />
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ContactPage;
